import { IContentSection } from '../interfaces/card-info.interface';

// Long-form SEO content for /app-development-company, grouped by background band.
// Headings use the site convention: lowercase with <span class="gradient-text"> accents.

// Band 1 (background-1): below the hero — company intro, custom dev, iOS.
export const DEV_SECTIONS_INTRO: IContentSection[] = [
  {
    headingHtml: 'mobile app<br><span class="gradient-text">development</span><br><span class="gradient-text">company</span><br>in new york',
    lead: [
      'SemNexus is a leading New York mobile app development company specializing in custom iOS, Android, AI-powered, and cross-platform applications. Our team helps startups, entrepreneurs, and established businesses transform ideas into scalable digital products through strategic planning, user-focused design, and agile software development.',
    ],
    outro: [
      'Whether you’re launching a startup MVP, building an enterprise platform, or modernizing an existing application, our New York mobile app development services are designed to accelerate time-to-market while maintaining performance, security, and scalability. We combine technical expertise with growth-focused product strategy to build applications that not only function properly but also support long-term business goals.',
    ],
  },
  {
    headingHtml: 'custom <span class="gradient-text">app</span><br><span class="gradient-text">development</span><br>services',
    lead: [
      'Every successful application begins with understanding the problem it solves.',
      'Our custom app development services are tailored to each client’s business objectives, target audience, monetization strategy, and technical requirements. Rather than forcing businesses into rigid templates, we build solutions specifically designed around their workflows, customer needs, and long-term growth plans.',
      'Our development team handles everything from discovery and planning to design, development, testing, deployment, and ongoing maintenance. This end-to-end approach allows businesses to work with a single development partner throughout the entire software lifecycle.',
      'Services include:',
    ],
    bullets: [
      'Custom mobile app development',
      'SaaS development',
      'Web application development',
      'Enterprise software development',
      'Startup MVP development',
      'API integrations',
      'Backend development',
      'Cloud infrastructure',
    ],
  },
  {
    headingHtml: '<span class="gradient-text">iOS app</span> development',
    lead: [
      'Building for Apple’s ecosystem requires more than writing code. It requires understanding Apple’s design standards, App Store requirements, performance expectations, and security guidelines.',
      'Our iOS app development team creates high-performance applications for iPhone, iPad, Apple Watch, and other Apple devices. We focus on intuitive user experiences, efficient architecture, and seamless integration with Apple’s ecosystem.',
    ],
    outro: [
      'Whether you need a consumer-facing mobile application, subscription platform, marketplace, healthcare solution, or business application, our team develops scalable iOS products built for long-term success.',
    ],
  },
];

// Band 2 (background-2): Android, cross-platform, AI.
export const DEV_SECTIONS_PLATFORM: IContentSection[] = [
  {
    headingHtml: '<span class="gradient-text">android app</span> development',
    lead: [
      'Android continues to be the largest mobile operating system in the world, making it essential for businesses seeking maximum market reach.',
      'Our Android app development services focus on creating secure, scalable applications optimized for the diverse Android ecosystem. We build applications capable of performing across a wide range of devices while maintaining a consistent user experience.',
    ],
    outro: [
      'From startup MVPs to enterprise applications, our Android developers leverage modern frameworks and development practices to deliver reliable, high-performance mobile solutions.',
    ],
  },
  {
    headingHtml: '<span class="gradient-text">cross-platform</span><br>app<br>development',
    lead: [
      'For many businesses, speed and efficiency are critical.',
      'Our cross-platform app development services allow companies to launch applications across iOS and Android using a shared codebase. This approach can significantly reduce development costs while accelerating product launches.',
      'Using technologies such as React Native and other modern frameworks, we build applications that provide near-native performance while maintaining flexibility and scalability.',
    ],
    outro: [
      'Cross-platform development is often an ideal solution for startups seeking to validate product-market fit before investing in separate native applications.',
    ],
  },
  {
    headingHtml: '<span class="gradient-text">AI app</span> development',
    lead: [
      'Artificial intelligence is rapidly transforming the way businesses operate and interact with customers.',
      'SEMNexus develops AI-powered applications that leverage machine learning, large language models, automation systems, predictive analytics, recommendation engines, and intelligent workflows. Our AI app development services help organizations improve efficiency, reduce manual work, and create differentiated customer experiences.',
    ],
    outro: [
      'Whether you are building an AI chatbot, automation platform, predictive analytics system, AI marketplace, or generative AI product, our team can design and develop custom solutions aligned with your business objectives.',
    ],
  },
];

// Band 3 (background-3): UI/UX, agile process, and the bottom SEO block.
export const DEV_SECTIONS_PROCESS: IContentSection[] = [
  {
    headingHtml: '<span class="gradient-text">UI/UX design</span> &amp; app prototyping',
    lead: [
      'Great applications begin with great user experiences.',
      'Before development starts, our design team creates wireframes, user flows, interactive prototypes, and high-fidelity designs that allow stakeholders to validate concepts before significant development resources are invested.',
      'This process reduces risk, improves communication, and helps ensure that the final product aligns with both user expectations and business objectives.',
      'Our design services include:',
    ],
    bullets: [
      'Wireframing',
      'Interactive prototyping',
      'User experience design',
      'User interface design',
      'User testing',
      'Design systems',
    ],
  },
  {
    headingHtml: 'agile mobile<br>app<br><span class="gradient-text">development</span><br><span class="gradient-text">process</span>',
    lead: [
      'Our development process follows an agile methodology designed to maximize transparency, flexibility, and speed. Projects are developed in structured sprints, allowing clients to review progress regularly and provide feedback throughout development.',
      'Our process includes:',
    ],
    steps: [
      { term: 'Discovery', detail: 'Business analysis, market research, requirements gathering, and technical planning.' },
      { term: 'Design', detail: 'Wireframes, prototypes, user testing, and interface design.' },
      { term: 'Development', detail: 'Agile sprint-based development for mobile and web platforms.' },
      { term: 'Quality Assurance', detail: 'Comprehensive testing across devices, operating systems, and use cases.' },
      { term: 'Deployment', detail: 'App Store and Google Play submission, launch support, and monitoring.' },
      { term: 'Maintenance', detail: 'Ongoing updates, feature enhancements, security improvements, and scaling support.' },
    ],
  },
  {
    headingHtml: 'why choose <span class="gradient-text">semnexus</span> as your app development partner?',
    lead: [
      'Choosing the right app development company can determine whether a product succeeds or fails. SEMNexus combines strategic planning, UI/UX design, mobile app development, AI development, quality assurance, and growth expertise under one roof.',
    ],
    outro: [
      'Unlike traditional development firms that focus solely on coding, our team understands the full product lifecycle—from initial concept and MVP development to user acquisition, App Store Optimization, retention, and long-term growth. This integrated approach allows clients to move faster, reduce risk, and build products designed for both technical success and commercial viability.',
    ],
  },
];
